// Cleaner Service - Manage cleaner profiles and documents

import { Injectable } from '@angular/core';
import { Observable, from, of } from 'rxjs';
import { catchError, map, switchMap, take, timeout } from 'rxjs/operators';
import { getSupabaseClient } from '../../core/supabase/supabase.client';
import { CleanerProfile, CleanerDocument } from '../../models';
import { formatNamePart } from '../utils/person-name';
import { StorageService, UploadResult } from '../../core/services';

function isAbortError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as { name?: string; message?: string };
  return e.name === 'AbortError' || (typeof e.message === 'string' && e.message.includes('AbortError'));
}

export interface PublicCleaner {
  userId: string;
  firstName: string;
  lastName: string;
  createdAt?: Date;
  avatarUrl?: string;
  bio?: string;
  spokenLanguages: string[];
  hourlyRateB2C: number;
  fixedRateB2B?: number;
  baseLocation?: any;
  maxTravelDistance: number;
  serviceAreas: string[];
  serviceCategories: string[];
  serviceSubcategories: string[];
  rating?: number;
  reviewCount: number;
  totalJobsCompleted: number;
  commissionTier: string;
  profileCompletionPercent: number;
  profileIsComplete: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class CleanerService {
  private supabase = getSupabaseClient();

  constructor(private storageService: StorageService) {}

  /**
   * List public cleaners using RPC function
   */
  listPublicCleaners(): Observable<PublicCleaner[]> {
    return from(
      this.supabase.rpc('list_public_cleaners')
    ).pipe(
      switchMap(({ data, error }) => {
        if (error) {
          console.error('Error listing public cleaners:', error);
          return of([]);
        }
        // Map database format (snake_case) to frontend format (camelCase)
        const cleanersData = (data as unknown[]) || [];
        const cleaners = cleanersData.map((cleaner) => this.mapDatabasePublicCleaner(cleaner));
        return this.withPublicReviewAggregates(cleaners);
      }),
      catchError((error) => {
        console.error('Error in listPublicCleaners:', error);
        return of([]);
      })
    );
  }

  /**
   * Paginated public cleaner directory for infinite scroll.
   * Prefers list_public_cleaners_page; falls back to list_public_cleaners + client slice.
   */
  listPublicCleanersPage(params: {
    limit?: number;
    offset?: number;
    area?: string | null;
    service?: string | null;
    sort?: string | null;
    search?: string | null;
    category?: string | null;
  } = {}): Observable<{ cleaners: PublicCleaner[]; totalCount: number; hasMore: boolean }> {
    const limit = Math.max(params.limit ?? 20, 1);
    const offset = Math.max(params.offset ?? 0, 0);
    const sort = params.sort || 'relevant';

    return from(
      this.supabase.rpc('list_public_cleaners_page', {
        p_limit: limit,
        p_offset: offset,
        p_area: params.area?.trim() || null,
        p_service: params.service?.trim() || null,
        p_sort: sort,
        p_search: params.search?.trim() || null,
        p_category: params.category?.trim() || null,
      })
    ).pipe(
      switchMap(({ data, error }) => {
        if (error) {
          console.warn('list_public_cleaners_page unavailable, falling back:', error.message);
          return this.listPublicCleaners().pipe(
            map((all) => {
              let filtered = [...all];
              const search = params.search?.trim().toLowerCase();
              if (search) {
                filtered = filtered.filter(
                  (c) =>
                    c.firstName.toLowerCase().includes(search) ||
                    c.lastName.toLowerCase().includes(search) ||
                    c.baseLocation?.city?.toLowerCase().includes(search) ||
                    c.serviceAreas.some((a) => a.toLowerCase().includes(search))
                );
              }
              if (params.category) {
                filtered = filtered.filter((c) =>
                  c.serviceCategories.includes(params.category as string)
                );
              }
              if (params.area?.trim()) {
                const area = params.area.trim();
                filtered = filtered.filter((c) =>
                  c.serviceAreas.some((a) => a.trim() === area)
                );
              }
              if (params.service?.trim()) {
                const serviceMap: Record<string, string[]> = {
                  home: ['REGULAR_CLEANING', 'DEEP_CLEANING', 'MOVE_IN_OUT', 'IRONING_SERVICE'],
                  commercial: ['OFFICE_CLEANING', 'BUSINESS_DEEP_CLEANING'],
                  window: ['WINDOW_CLEANING', 'BUSINESS_WINDOWS'],
                  solar: ['RESIDENTIAL_SOLAR_PANELS', 'BUSINESS_SOLAR_PANELS'],
                  car: ['CAR_DETAILING'],
                };
                const needed = serviceMap[params.service.trim().toLowerCase()];
                if (needed) {
                  filtered = filtered.filter((c) =>
                    c.serviceSubcategories.some((s) => needed.includes(s))
                  );
                }
              }
              filtered.sort((a, b) => {
                if (sort === 'relevant' || !sort || sort === 'completion') {
                  if (a.profileIsComplete !== b.profileIsComplete) {
                    return Number(b.profileIsComplete) - Number(a.profileIsComplete);
                  }
                  if (a.profileCompletionPercent !== b.profileCompletionPercent) {
                    return b.profileCompletionPercent - a.profileCompletionPercent;
                  }
                  const aPhoto = a.avatarUrl?.trim() ? 1 : 0;
                  const bPhoto = b.avatarUrl?.trim() ? 1 : 0;
                  if (aPhoto !== bPhoto) return bPhoto - aPhoto;
                  const aRated = (a.rating || 0) > 0 || a.reviewCount > 0 ? 1 : 0;
                  const bRated = (b.rating || 0) > 0 || b.reviewCount > 0 ? 1 : 0;
                  if (aRated !== bRated) return bRated - aRated;
                  if ((b.rating || 0) !== (a.rating || 0)) {
                    return (b.rating || 0) - (a.rating || 0);
                  }
                  if (b.reviewCount !== a.reviewCount) return b.reviewCount - a.reviewCount;
                  if (b.totalJobsCompleted !== a.totalJobsCompleted) {
                    return b.totalJobsCompleted - a.totalJobsCompleted;
                  }
                  const aBio = a.bio?.trim() ? 1 : 0;
                  const bBio = b.bio?.trim() ? 1 : 0;
                  if (aBio !== bBio) return bBio - aBio;
                  const aCreated = a.createdAt?.getTime() ?? 0;
                  const bCreated = b.createdAt?.getTime() ?? 0;
                  return aCreated - bCreated;
                }
                switch (sort) {
                  case 'rating':
                    if ((b.rating || 0) !== (a.rating || 0)) {
                      return (b.rating || 0) - (a.rating || 0);
                    }
                    return b.reviewCount - a.reviewCount;
                  case 'jobs':
                    return b.totalJobsCompleted - a.totalJobsCompleted;
                  case 'price_asc':
                    return a.hourlyRateB2C - b.hourlyRateB2C;
                  case 'price_desc':
                    return b.hourlyRateB2C - a.hourlyRateB2C;
                  default:
                    return (b.rating || 0) - (a.rating || 0);
                }
              });
              const slice = filtered.slice(offset, offset + limit);
              return {
                cleaners: slice,
                totalCount: filtered.length,
                hasMore: offset + slice.length < filtered.length,
              };
            })
          );
        }

        const rows = (data as unknown[]) || [];
        const cleaners = rows.map((row) => this.mapDatabasePublicCleaner(row));
        const totalCount =
          rows.length > 0 &&
          typeof (rows[0] as { total_count?: number }).total_count === 'number'
            ? Number((rows[0] as { total_count: number }).total_count)
            : cleaners.length + offset;

        return this.withPublicReviewAggregates(cleaners).pipe(
          map((withReviews) => ({
            cleaners: withReviews,
            totalCount,
            hasMore: offset + withReviews.length < totalCount,
          }))
        );
      }),
      catchError((error) => {
        console.error('Error in listPublicCleanersPage:', error);
        return of({ cleaners: [], totalCount: 0, hasMore: false });
      })
    );
  }

  /**
   * Get a single public cleaner by ID (for detail page).
   * Uses get_public_cleaner_by_id so quoted/job partners can view a cleaner who
   * is approved and visible but currently unavailable (excluded from the directory list).
   * Completes after first emission and times out after 10s so the UI never stays stuck loading.
   */
  getPublicCleanerById(cleanerId: string): Observable<PublicCleaner | null> {
    return from(
      this.supabase.rpc('get_public_cleaner_by_id', { p_cleaner_id: cleanerId })
    ).pipe(
      switchMap(({ data, error }) => {
        if (error) {
          console.error('Error in getPublicCleanerById:', error);
          return this.listPublicCleaners().pipe(
            map((cleaners) => cleaners.find((c) => c.userId === cleanerId) ?? null),
          );
        }
        const rows = (data as unknown[]) || [];
        const row = rows[0];
        if (!row) return of(null);
        return this.withPublicReviewAggregates([this.mapDatabasePublicCleaner(row)]).pipe(
          map((cleaners) => cleaners[0] ?? null),
        );
      }),
      take(1),
      timeout(10000),
      catchError((err) => {
        console.error('Error in getPublicCleanerById:', err);
        return of(null);
      })
    );
  }

  /**
   * Overlay live public review aggregates onto directory/detail cleaner rows.
   */
  private withPublicReviewAggregates(cleaners: PublicCleaner[]): Observable<PublicCleaner[]> {
    const cleanerIds = cleaners.map((c) => c.userId).filter(Boolean);
    if (cleanerIds.length === 0) return of(cleaners);

    return from(
      this.supabase
        .from('reviews')
        .select('reviewee_id,rating')
        .in('reviewee_id', cleanerIds)
        .eq('is_public', true)
    ).pipe(
      map(({ data: reviewsData, error: reviewsError }) => {
        if (reviewsError) {
          console.error('Error loading review aggregates:', reviewsError);
          return cleaners;
        }

        const statsByCleaner = new Map<string, { sum: number; count: number }>();
        for (const row of (reviewsData as { reviewee_id?: string; rating?: number }[]) || []) {
          const revieweeId = row.reviewee_id;
          const rating = Number(row.rating);
          if (!revieweeId || !Number.isFinite(rating)) continue;
          const current = statsByCleaner.get(revieweeId) ?? { sum: 0, count: 0 };
          current.sum += rating;
          current.count += 1;
          statsByCleaner.set(revieweeId, current);
        }

        return cleaners.map((cleaner) => {
          const stats = statsByCleaner.get(cleaner.userId);
          if (!stats || stats.count === 0) return cleaner;
          return {
            ...cleaner,
            rating: Math.round((stats.sum / stats.count) * 10) / 10,
            reviewCount: stats.count,
          };
        });
      }),
      catchError((error) => {
        console.error('Error aggregating review data:', error);
        return of(cleaners);
      })
    );
  }

  /**
   * Map database row to PublicCleaner model
   * Supports both 'id' and 'user_id' for the cleaner's user id (RPC may return either).
   */
  private mapDatabasePublicCleaner(data: Record<string, unknown> | unknown): PublicCleaner {
    const row = (data ?? {}) as Record<string, unknown>;
    return {
      userId: String(row['user_id'] ?? row['id'] ?? ''),
      firstName: formatNamePart(typeof row['first_name'] === 'string' ? row['first_name'] : undefined),
      lastName: formatNamePart(typeof row['last_name'] === 'string' ? row['last_name'] : undefined),
      createdAt: row['created_at'] ? new Date(String(row['created_at'])) : undefined,
      avatarUrl: typeof row['avatar_url'] === 'string' ? row['avatar_url'] : undefined,
      bio: typeof row['bio'] === 'string' ? row['bio'] : undefined,
      spokenLanguages: Array.isArray(row['spoken_languages']) ? (row['spoken_languages'] as string[]) : [],
      hourlyRateB2C: Number(row['hourly_rate_b2c']),
      fixedRateB2B: row['fixed_rate_b2b'] != null ? Number(row['fixed_rate_b2b']) : undefined,
      baseLocation: row['base_location'] || undefined,
      maxTravelDistance: Number(row['max_travel_distance'] ?? 0),
      serviceAreas: Array.isArray(row['service_areas']) ? (row['service_areas'] as string[]) : [],
      serviceCategories: Array.isArray(row['service_categories']) ? (row['service_categories'] as string[]) : [],
      serviceSubcategories: Array.isArray(row['service_subcategories']) ? (row['service_subcategories'] as string[]) : [],
      rating: row['rating'] != null ? Number(row['rating']) : undefined,
      reviewCount: Number(row['review_count'] ?? 0),
      totalJobsCompleted: Number(row['total_jobs_completed'] ?? 0),
      commissionTier: typeof row['commission_tier'] === 'string' ? row['commission_tier'] : 'promotional',
      profileCompletionPercent: Number(row['profile_completion_percent'] ?? 0),
      profileIsComplete: Boolean(row['profile_is_complete']),
    };
  }

  /**
   * Get cleaner profile by user ID
   */
  getCleanerProfile(userId: string): Observable<CleanerProfile | null> {
    return from(
      this.supabase
        .from('cleaner_profiles')
        .select('*')
        .eq('user_id', userId)
        .single()
    ).pipe(
      map(({ data, error }) => {
        if (error) {
          console.error('Error fetching cleaner profile:', error);
          return null;
        }
        return data ? this.mapDatabaseProfile(data) : null;
      }),
      catchError((error) => {
        console.error('Error in getCleanerProfile:', error);
        return of(null);
      })
    );
  }

  /**
   * Update cleaner profile
   */
  updateCleanerProfile(
    userId: string,
    updates: Partial<CleanerProfile>
  ): Observable<CleanerProfile | null> {
    // Map frontend model to database columns
    const dbUpdates: any = {};

    if (updates.bio !== undefined) dbUpdates.bio = updates.bio;
    if (updates.spokenLanguages !== undefined) dbUpdates.spoken_languages = updates.spokenLanguages;
    if (updates.hourlyRateB2C !== undefined) dbUpdates.hourly_rate_b2c = updates.hourlyRateB2C;
    if (updates.fixedRateB2B !== undefined) dbUpdates.fixed_rate_b2b = updates.fixedRateB2B;
    if (updates.isAvailable !== undefined) dbUpdates.is_available = updates.isAvailable;
    if (updates.profileVisibility !== undefined) dbUpdates.profile_visibility = updates.profileVisibility;
    if (updates.approvalStatus !== undefined) dbUpdates.approval_status = updates.approvalStatus;
    if (updates.baseLocation !== undefined) dbUpdates.base_location = updates.baseLocation;
    if (updates.maxTravelDistance !== undefined) dbUpdates.max_travel_distance = updates.maxTravelDistance;
    if (updates.kmCompensation !== undefined) dbUpdates.km_compensation = updates.kmCompensation;
    if (updates.kmCompensationIncluded !== undefined) dbUpdates.km_compensation_included = updates.kmCompensationIncluded;
    if (updates.serviceAreas !== undefined) dbUpdates.service_areas = updates.serviceAreas;
    if (updates.serviceCategories !== undefined) dbUpdates.service_categories = updates.serviceCategories;
    if (updates.serviceSubcategories !== undefined) dbUpdates.service_subcategories = updates.serviceSubcategories;
    if (updates.iban !== undefined) dbUpdates.iban = updates.iban;
    if (updates.accountHolderName !== undefined) {
      dbUpdates.account_holder_name = updates.accountHolderName.trim() || null;
    }
    if (updates.vatNumber !== undefined) dbUpdates.vat_number = updates.vatNumber;
    if (updates.vatExempt !== undefined) dbUpdates.vat_exempt = updates.vatExempt;
    if (updates.businessName !== undefined) dbUpdates.business_name = updates.businessName;
    if (updates.businessAddress !== undefined) dbUpdates.business_address = updates.businessAddress;
    if (updates.insuranceExpiryDate !== undefined) dbUpdates.insurance_expiry_date = updates.insuranceExpiryDate;
    if (updates.onboardingProgress !== undefined) dbUpdates.onboarding_progress = updates.onboardingProgress;

    return from(
      this.supabase
        .from('cleaner_profiles')
        // @ts-ignore - Database types are out of sync with actual schema
        .update(dbUpdates)
        .eq('user_id', userId)
        .select()
        .single()
    ).pipe(
      map(({ data, error }) => {
        if (error) {
          console.error('Error updating cleaner profile:', error);
          return null;
        }
        return data ? this.mapDatabaseProfile(data) : null;
      }),
      catchError((error) => {
        console.error('Error in updateCleanerProfile:', error);
        return of(null);
      })
    );
  }

  /**
   * Get cleaner documents
   */
  getCleanerDocuments(userId: string): Observable<CleanerDocument[]> {
    return from(
      this.supabase
        .from('cleaner_documents')
        .select('*')
        .eq('cleaner_id', userId)
        .order('uploaded_at', { ascending: false })
    ).pipe(
      map(({ data, error }) => {
        if (error) {
          if (!isAbortError(error)) {
            console.error('Error fetching cleaner documents:', error);
          }
          return [];
        }
        return data?.map(this.mapDatabaseDocument) || [];
      }),
      catchError((error) => {
        if (!isAbortError(error)) {
          console.error('Error in getCleanerDocuments:', error);
        }
        return of([]);
      })
    );
  }

  /**
   * Upload cleaner document
   */
  uploadDocument(
    userId: string,
    type: 'id' | 'insurance' | 'certificate' | 'vat_proof',
    file: File
  ): Observable<CleanerDocument | null> {
    return this.storageService.uploadCleanerDocument(userId, type, file).pipe(
      switchMap((uploadResult: UploadResult) => {
        // After upload, create document record in database
        return from(
          this.supabase
            .from('cleaner_documents')
            // @ts-ignore - Database types are out of sync with actual schema
            .insert({
              cleaner_id: userId,
              type,
              file_name: file.name,
              file_url: uploadResult.path,
              file_size: file.size,
              mime_type: file.type,
            })
            .select()
            .single()
        );
      }),
      map(({ data, error }: any) => {
        if (error) {
          console.error('Error creating document record:', error);
          return null;
        }
        return data ? this.mapDatabaseDocument(data) : null;
      }),
      catchError((error) => {
        console.error('Error in uploadDocument:', error);
        return of(null);
      })
    );
  }

  /**
   * Delete cleaner document
   */
  deleteDocument(documentId: string): Observable<boolean> {
    return from(
      this.supabase
        .from('cleaner_documents')
        .delete()
        .eq('id', documentId)
    ).pipe(
      map(({ error }) => {
        if (error) {
          console.error('Error deleting document:', error);
          return false;
        }
        return true;
      }),
      catchError((error) => {
        console.error('Error in deleteDocument:', error);
        return of(false);
      })
    );
  }

  /**
   * Get pending cleaners for admin approval
   */
  getPendingCleaners(): Observable<CleanerProfile[]> {
    return from(
      this.supabase
        .from('cleaner_profiles')
        .select('*')
        .eq('approval_status', 'pending_admin_review')
        .order('updated_at', { ascending: false })
    ).pipe(
      map(({ data, error }) => {
        if (error) {
          console.error('Error fetching pending cleaners:', error);
          return [];
        }
        return data?.map(this.mapDatabaseProfile) || [];
      }),
      catchError((error) => {
        console.error('Error in getPendingCleaners:', error);
        return of([]);
      })
    );
  }

  /**
   * Map database row to CleanerProfile model
   */
  private mapDatabaseProfile(data: any): CleanerProfile {
    return {
      userId: data.user_id,
      bio: data.bio || undefined,
      spokenLanguages: data.spoken_languages || [],
      hourlyRateB2C: data.hourly_rate_b2c,
      fixedRateB2B: data.fixed_rate_b2b || undefined,
      isAvailable: data.is_available,
      profileVisibility: data.profile_visibility,
      approvalStatus: data.approval_status,
      approvedAt: data.approved_at ? new Date(data.approved_at) : undefined,
      approvedBy: data.approved_by || undefined,
      rejectionReason: data.rejection_reason || undefined,
      baseLocation: data.base_location || undefined,
      maxTravelDistance: data.max_travel_distance,
      kmCompensation: data.km_compensation || undefined,
      kmCompensationIncluded: data.km_compensation_included,
      serviceAreas: data.service_areas || [],
      serviceCategories: data.service_categories || [],
      serviceSubcategories: data.service_subcategories || [],
      rating: data.rating || undefined,
      reviewCount: data.review_count,
      totalJobsCompleted: data.total_jobs_completed,
      commissionTier: data.commission_tier,
      iban: data.iban || undefined,
      accountHolderName: data.account_holder_name || undefined,
      vatNumber: data.vat_number || undefined,
      vatExempt: data.vat_exempt ?? false,
      businessName: data.business_name || undefined,
      businessAddress: data.business_address || undefined,
      insuranceExpiryDate: data.insurance_expiry_date ? new Date(data.insurance_expiry_date) : undefined,
      insuranceVerified: data.insurance_verified,
      stripeAccountId: data.stripe_account_id || undefined,
      stripeAccountStatus: data.stripe_account_status || undefined,
      onboardingProgress: data.onboarding_progress || undefined,
      documents: [], // Loaded separately
      createdAt: new Date(data.created_at),
      updatedAt: new Date(data.updated_at),
    };
  }

  /**
   * Map database row to CleanerDocument model
   */
  private mapDatabaseDocument(data: any): CleanerDocument {
    return {
      id: data.id,
      cleanerId: data.cleaner_id,
      type: data.type,
      fileName: data.file_name,
      fileUrl: data.file_url,
      fileSize: data.file_size || undefined,
      mimeType: data.mime_type || undefined,
      uploadedAt: new Date(data.uploaded_at),
      verified: data.verified,
      verifiedAt: data.verified_at ? new Date(data.verified_at) : undefined,
      verifiedBy: data.verified_by || undefined,
      rejectionReason: data.rejection_reason || undefined,
      expiryDate: data.expiry_date ? new Date(data.expiry_date) : undefined,
    };
  }
}
